import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Paper,
  TextField,
  Typography,
  Box,
  Divider,
  Stack,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { CONSTANTS } from "../../../../library/common/constant";
import styles from "./AddNewBook.module.scss";
import { api } from "../../../../services";
import MetaFields from "../../../../library/common/MetaFields";
import FileUploadPreview from "../../../../library/common/FileUploadPreview";
import AuthorFields from "../../../../library/common/AuthorFields";
import BasicInformationFields from "../../../../library/common/BasicInformationFields";
import { debounce, throttle } from "lodash";
import CustomDialog from "../../../../library/common/CustomDialog";
import { exportToJson, importJsonDraft } from "../../../../library/common/exportImportDraft";
import { CloudDownload, CloudUpload } from "@mui/icons-material";

const cleanDraftData = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (obj instanceof File) {
    return {
      name: obj.name,
      size: obj.size,
      type: obj.type,
      isDraftPlaceholder: true,
    };
  }
  if (Array.isArray(obj)) {
    return obj
      .map(item => cleanDraftData(item))
      .filter(item => item !== undefined);
  }
  if (typeof obj === "object") {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      const cleanedVal = cleanDraftData(value);
      if (cleanedVal !== undefined) {
        cleaned[key] = cleanedVal;
      }
    }
    return cleaned;
  }
  return obj;
};

const useFileUpload = (setUpdatedData, setErrors) => {
  const handleFileChange = useCallback(
    (e) => {
      const { name, files } = e.target;

      if (files?.length > 0) {
        const file = files[0];

        // Check if file is larger than 10MB (10 * 1024 * 1024 bytes)
        if (file.size > 10 * 1024 * 1024) {
          setErrors((prev) => ({
            ...prev,
            [name]:
              "Please upload a file up to 10MB only, or you can share the book file url",
          }));
          setUpdatedData((prev) => ({ ...prev, [name]: null }));
          return;
        } else {
          setErrors((prev) => ({ ...prev, [name]: "" })); // clear error
        }

        setUpdatedData((prev) => ({ ...prev, [name]: file }));
      } else {
        setUpdatedData((prev) => ({ ...prev, [name]: null }));
      }
    },
    [setUpdatedData, setErrors]
  );

  return { handleFileChange };
};

const {
  ADD_BOOK_TITLE,
  ADD_BOOK_DESCRIPTION,
  OTHER_BOOK_DETAILS_SECTION,
  META_DETAILS,
  THROTTLE_DURATION,
} = CONSTANTS;

const BookForm = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const location = useLocation();
  const isEditMode = Boolean(slug);

  // Determine type from URL query param (?type=literature) — defaults to "book"
  const queryType = new URLSearchParams(location.search).get("type") || "book";
  const isArticle = queryType === "literature";
  const formTypeLabel = isArticle ? "Article" : "Granth";

  const [initialData, setInitialData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEditMode);
  const [apiError, setApiError] = useState("");
  const [updatedData, setUpdatedData] = useState({ type: queryType });
  const [isLoading, setIsLoading] = useState(false);
  const formRef = useRef(null);
  const importInputRef = useRef(null);

  // Draft and Toast states
  const [toast, setToast] = useState({ open: false, message: "", severity: "error" });
  const [draftDialogOpen, setDraftDialogOpen] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);

  const { handleFileChange } = useFileUpload(setUpdatedData, setErrors);

  const getFieldValue = useCallback(
    (fieldPath) => {
      const keys = fieldPath.split(".");
      let value = updatedData[fieldPath];
      if (value === undefined) {
        value = keys.reduce(
          (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
          initialData
        );
      }
      return value;
    },
    [updatedData, initialData]
  );

  const validateSlug = useCallback(async (slugVal) => {
    if (updatedData?.slug === initialData?.slug) return true;
    try {
      const { data } = await api.get(`slug/book/${slugVal}`);
      if (!data) setErrors((prev) => ({ ...prev, slug: "Slug is not unique" }));
      return data;
    } catch {
      setErrors((prev) => ({ ...prev, slug: "Slug validation failed" }));
      return false;
    }
  }, [updatedData?.slug, initialData?.slug]);

  const debouncedValidateSlug = useMemo(
    () =>
      debounce(async (slugVal) => {
        await validateSlug(slugVal);
      }, 500),
    [validateSlug]
  );

  useEffect(() => {
    return () => {
      debouncedValidateSlug.cancel();
    };
  }, [debouncedValidateSlug]);

  const focusFirstError = (newErrors) => {
    const firstErrorKey = Object.keys(newErrors)[0];
    if (!firstErrorKey) return;

    let element = document.getElementsByName(firstErrorKey)[0];
    if (!element) {
      element = document.getElementById(firstErrorKey);
    }
    if (!element) {
      element = document.querySelector(`[name="${firstErrorKey}"]`);
    }
    if (!element) {
      element = document.querySelector(
        `input[name*="${firstErrorKey}"], select[name*="${firstErrorKey}"], textarea[name*="${firstErrorKey}"]`
      );
    }

    if (element) {
      element.focus();
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // Export current page books as JSON
  const handleExport = () => {
    const ok = exportToJson(cleanDraftData(updatedData), `books_${queryType}`);
    setToast({
      open: true,
      message: ok ? "Exported successfully!" : "Export failed.",
      severity: ok ? "success" : "error",
    });
  };

  // Trigger hidden file input
  const handleImportClick = () => importInputRef.current?.click();

  const TAB_ADD_PATHS = ["/book/add", "/article/add?type=literature"];

  // Handle file selected for import
  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be re-selected
    e.target.value = null;
    importJsonDraft(
      file,
      "draft_book",
      navigate,
      TAB_ADD_PATHS[isArticle ? 1 : 0],
      (msg) => setToast({ open: true, message: msg, severity: "success" }),
      (msg) => setToast({ open: true, message: msg, severity: "error" })
    );
  };

  const handleApiError = (error) => {
    const data = error.response?.data;
    const message = data?.message || error.message || "An error occurred.";
    const errorsPayload = data?.errors;

    let displayMessage = message;
    if (errorsPayload) {
      if (typeof errorsPayload === "string") {
        displayMessage = errorsPayload;
      } else if (typeof errorsPayload === "object") {
        const messages = [];
        for (const [key, value] of Object.entries(errorsPayload)) {
          if (value && typeof value === "object") {
            messages.push(value.message || JSON.stringify(value));
          } else {
            messages.push(String(value));
          }
        }
        displayMessage = messages.join(", ");
      }
    }

    const isSizeError = /size|large|limit|upload/i.test(displayMessage) || /size|large|limit|upload/i.test(message);
    if (isSizeError) {
      const isImage = /image|cover|authorImage|featured/i.test(displayMessage) || /image|cover|authorImage|featured/i.test(message) || /image|cover|authorImage|featured/i.test(JSON.stringify(errorsPayload));
      const isFile = /file|pdf/i.test(displayMessage) || /file|pdf/i.test(message) || /file|pdf/i.test(JSON.stringify(errorsPayload));
      if (isImage) {
        displayMessage = "Use URL image or image size is large";
      } else if (isFile) {
        displayMessage = "Use URL file or file size is large";
      } else {
        displayMessage = "Use URL image or file size is large";
      }
    }

    setToast({
      open: true,
      message: displayMessage,
      severity: "error",
    });

    if (errorsPayload) {
      const parsedErrors = {};
      if (typeof errorsPayload === "string") {
        const parts = errorsPayload.split(",");
        parts.forEach((part) => {
          const match = part.match(/"([^"]+)"\s+(.+)/);
          if (match) {
            const field = match[1];
            const desc = match[2];
            parsedErrors[field] = `Server: "${field}" ${desc}`;
          }
        });
      } else if (typeof errorsPayload === "object") {
        for (const [key, value] of Object.entries(errorsPayload)) {
          if (value && typeof value === "object") {
            parsedErrors[key] = value.message || String(value);
          } else {
            parsedErrors[key] = String(value);
          }
        }
      }
      if (Object.keys(parsedErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...parsedErrors }));
        focusFirstError(parsedErrors);
      }
    } else if (message.includes("Duplicate Key Error") || error.response?.status === 409) {
      if (message.includes("slug")) {
        setErrors((prev) => ({ ...prev, slug: "Slug already exists." }));
        focusFirstError({ slug: true });
      }
    }
  };

  const validateForm = async () => {
    const newErrors = {};
    const slugVal = (getFieldValue("slug") || "").trim();
    if (!slugVal) {
      newErrors["slug"] = "Slug is required";
    } else if (!/^[A-Za-z0-9_-]+$/.test(slugVal)) {
      newErrors["slug"] =
        "Slug can only contain letters, numbers, hyphens, and underscores";
    } else debouncedValidateSlug(slugVal);

    if (!(getFieldValue("title.en") || "").trim()) {
      newErrors["title.en"] = "English book title is required";
    }
    if (!(getFieldValue("title.hi") || "").trim()) {
      newErrors["title.hi"] = "Hindi book title is required";
    }
    const categoryVal = getFieldValue("categories");
    if (!Array.isArray(categoryVal) || categoryVal.length === 0) {
      newErrors["categories"] = "At least one category is required";
    }
    if (!(getFieldValue("languageId") || "").toString().trim()) {
      newErrors["languageId"] = "Language is required";
    }
    if (!(getFieldValue("shortDescription.en") || "").trim()) {
      newErrors["shortDescription.en"] =
        "Short description (English) is required";
    }
    if (!(getFieldValue("shortDescription.hi") || "").trim()) {
      newErrors["shortDescription.hi"] =
        "Short description (Hindi) is required";
    }
    if (!(getFieldValue("longDescription.en") || "").trim()) {
      newErrors["longDescription.en"] =
        "Long description (English) is required";
    }
    if (!(getFieldValue("longDescription.hi") || "").trim()) {
      newErrors["longDescription.hi"] = "Long description (Hindi) is required";
    }
    if (!(getFieldValue("author.name.en") || "").trim()) {
      newErrors["author.name.en"] = "Author name (English) is required";
    }
    if (!(getFieldValue("author.name.hi") || "").trim()) {
      newErrors["author.name.hi"] = "Author name (Hindi) is required";
    }

    const noOfPagesVal = getFieldValue("noOfPages");
    if (noOfPagesVal !== undefined && noOfPagesVal !== null && noOfPagesVal !== "") {
      const pagesNum = Number(noOfPagesVal);
      if (isNaN(pagesNum) || pagesNum <= 0 || !Number.isInteger(pagesNum)) {
        newErrors["noOfPages"] = "Number of pages must be a positive integer";
      }
    }

    const fileUrl = (getFieldValue("fileUrl") || "").trim();
    const file = updatedData["file"];
    if (!fileUrl && !file) {
      newErrors["fileUrl"] = "Book file URL or book file is required";
    } else if (file && file.isDraftPlaceholder) {
      newErrors["fileUrl"] = "Please re-upload the book file (draft files must be re-selected)";
    } else if (fileUrl && !/^https?:\/\/.+/.test(fileUrl)) {
      newErrors["fileUrl"] = "Please provide a valid URL for the book file";
    }

    const imageUrl = (getFieldValue("imageUrl") || "").trim();
    const image = updatedData["image"];
    if (!imageUrl && !image) {
      newErrors["imageUrl"] = "Book image or Book image url is required";
    } else if (image && image.isDraftPlaceholder) {
      newErrors["imageUrl"] = "Please re-upload the book image (draft images must be re-selected)";
    } else if (imageUrl && !/^https?:\/\/.+/.test(imageUrl)) {
      newErrors["imageUrl"] = "Please provide a valid URL for the book image";
    }

    const authorImage = (getFieldValue("author.image") || "").trim();
    if (authorImage && !/^https?:\/\/.+/.test(authorImage)) {
      newErrors["author.image"] =
        "Please provide a valid URL for the author image";
    }

    const metafieldsCanonical = (getFieldValue("metafields.canonical") || "").trim();
    if (metafieldsCanonical && !/^https?:\/\/.+/.test(metafieldsCanonical)) {
      newErrors["metafields.canonical"] = "Please provide a valid URL for canonical link";
    }

    const metafieldsImage = (getFieldValue("metafields.image") || "").trim();
    if (metafieldsImage && !/^https?:\/\/.+/.test(metafieldsImage)) {
      newErrors["metafields.image"] = "Please provide a valid URL for metafields image";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      focusFirstError(newErrors);
      setToast({ open: true, message: "Please correct the errors in the form.", severity: "error" });
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = useCallback(
    async (e) => {
      let { name, value, type, checked } = e.target;
      if (name === "slug" && typeof value === "string") {
        value = value.replace(/\s+/g, "-");
      }
      const newValue = type === "checkbox" ? checked : value;
      setUpdatedData((prev) => ({ ...prev, [name]: newValue }));
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
      if (name === "slug") {
        debouncedValidateSlug(newValue);
      }
    },
    [errors, debouncedValidateSlug]
  );

  // Handle form submission.
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const cleanPayload = (obj) => {
      if (obj === null || obj === undefined) return undefined;
      if (obj instanceof File) return obj;
      if (Array.isArray(obj)) {
        const cleaned = obj.map(cleanPayload).filter(v => v !== undefined);
        return cleaned.length > 0 ? cleaned : undefined;
      }
      if (typeof obj === "object") {
        const cleaned = {};
        let hasKeys = false;
        for (const [k, v] of Object.entries(obj)) {
          if (v === "" || v === null || v === undefined) {
            continue;
          }
          const cv = cleanPayload(v);
          if (cv !== undefined) {
            cleaned[k] = cv;
            hasKeys = true;
          }
        }
        return hasKeys ? cleaned : undefined;
      }
      return obj;
    };

    const cleanedData = cleanPayload(updatedData) || {};
    const formToSend = new FormData();
    for (const key in cleanedData) {
      if (cleanedData[key] && cleanedData[key].isDraftPlaceholder) {
        continue;
      }
      let value = cleanedData[key];
      if (key === "slug" && typeof value === "string") {
        value = value.replace(/\s+/g, "-").replace(/^-+|-+$/g, "");
      }
      if (
        typeof value === "object" &&
        !Array.isArray(value) &&
        !(value instanceof File)
      ) {
        formToSend.append(key, JSON.stringify(value));
      } else if (Array.isArray(value)) {
        formToSend.append(key, value.join(","));
      } else {
        formToSend.append(key, value);
      }
    }
    if (isEditMode) formToSend.append("verified", true);

    formRef.current = formToSend;

    try {
      if (isEditMode) {
        handleEdit();
      } else {
        handleAdd();
      }
    } catch (error) {
      console.error(
        "Error while submitting the form:",
        error.response || error
      );
      setToast({
        open: true,
        message: "An error occurred while submitting the form.",
        severity: "error",
      });
    }
  };

  const returnBack = () => {
    navigate("/book/list", { replace: true });
  };

  const handleAdd = throttle(async () => {
    try {
      setIsLoading(true);
      const form = formRef.current;
      await api.post("/books", form, { skipAlert: true });
      localStorage.removeItem("draft_book");
      returnBack();
    } catch (error) {
      console.error("Failed to post: ", error);
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const handleEdit = throttle(async () => {
    try {
      setIsLoading(true);
      const form = formRef.current;
      await api.patch(`/books/${initialData?._id}`, form, { skipAlert: true });
      returnBack();
    } catch (error) {
      console.error("Failed to save: ", error);
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  // Autosave draft effect
  useEffect(() => {
    if (isEditMode || Object.keys(updatedData).length === 0) return;

    const save = () => {
      try {
        const cleaned = cleanDraftData(updatedData);
        localStorage.setItem("draft_book", JSON.stringify({
          data: cleaned,
          timestamp: Date.now(),
        }));
      } catch (err) {
        console.error("Failed to save draft:", err);
      }
    };

    const timeout = setTimeout(save, 1000);
    return () => clearTimeout(timeout);
  }, [updatedData, isEditMode]);

  // Load draft check on mount
  useEffect(() => {
    if (isEditMode) return;
    try {
      const stored = localStorage.getItem("draft_book");
      if (stored) {
        const { data, timestamp } = JSON.parse(stored);
        const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
        const { type, ...hasValidFields } = data
        if (Date.now() - timestamp < THIRTY_DAYS && hasValidFields && Object.keys(hasValidFields).length > 0) {
          setPendingDraft(data);
          setDraftDialogOpen(true);
        } else {
          localStorage.removeItem("draft_book");
        }
      }
    } catch (err) {
      console.error("Failed to check draft:", err);
    }
  }, [isEditMode]);

  const handleRestoreDraft = () => {
    if (pendingDraft) {
      setUpdatedData(pendingDraft);
      setToast({ open: true, message: "Draft restored successfully!", severity: "success" });
    }
    setDraftDialogOpen(false);
    setPendingDraft(null);
  };

  const handleDeleteDraft = () => {
    localStorage.removeItem("draft_book");
    setToast({ open: true, message: "Draft deleted.", severity: "info" });
    setDraftDialogOpen(false);
    setPendingDraft(null);
  };

  const handleResetForm = () => {
    setUpdatedData({});
    localStorage.removeItem("draft_book");
    setErrors({});
    setToast({ open: true, message: "Form reset and draft cleared.", severity: "info" });
  };

  useEffect(() => {
    if (!isEditMode) return;
    const fetchBookDetails = async () => {
      try {
        const { data } = await api.get(`/books/${slug}`);
        if (data) {
          data.languageId = data.languageId?._id;
          data.imageUrl = data.image;
          data.categories = data.categories.map((cat) => cat._id);
          setInitialData(data);
        }
      } catch (error) {
        console.error("Error fetching book details:", error);
        setApiError("Failed to load book details.");
      } finally {
        setLoading(false);
      }
    };
    fetchBookDetails();
  }, [isEditMode, slug]);

  const getDate = (date) => {
    if (date) return new Date(date).toISOString().split("T")[0];
    return new Date().toISOString().split("T")[0];
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper className={styles.paperContainer}>
      <Typography variant="h5" gutterBottom>
        {isEditMode ? `Update ${formTypeLabel}` : (isArticle ? "Add Article" : ADD_BOOK_TITLE)}
      </Typography>
      <Typography variant="body2" gutterBottom>
        {isEditMode ? `Update the ${formTypeLabel.toLowerCase()} details below.` : (isArticle ? "Fill in the article details below. You can upload a PDF file for the article." : ADD_BOOK_DESCRIPTION)}
      </Typography>
      {!isEditMode ? (<Box display="flex" justifyContent="flex-end" gap={2}>
        <Button
          variant="outlined"
          color="info"
          onClick={() => handleImportClick(isArticle)}
          startIcon={<CloudDownload />}
        >
          Import {isArticle ? "Article" : "Book"}
        </Button>

        <Button
          variant="outlined"
          onClick={handleExport}
          startIcon={<CloudUpload />}
        >
          Export {isArticle ? "Article" : "Book"}
        </Button>
        {/* Hidden import file input */}
        <input
          type="file"
          accept="application/json"
          ref={importInputRef}
          style={{ display: "none" }}
          onChange={handleImportFile}
        />
      </Box>) : null}
      {apiError && <Typography color="error">{apiError}</Typography>}
      <form onSubmit={handleSubmit}>
        {/* Basic Information Section */}

        <BasicInformationFields
          updatedData={updatedData}
          formData={initialData}
          handleChange={handleChange}
          getFieldValue={getFieldValue}
          errors={errors}
          page="book"
        />
        <Divider variant="middle" className={styles.divider} />

        {/* Author Details Section */}
        <AuthorFields
          getFieldValue={getFieldValue}
          handleChange={handleChange}
          handleFileChange={handleFileChange}
          error={apiError}
        />

        <Divider variant="middle" className={styles.divider} />

        {/* Media Upload Section */}
        <FileUploadPreview
          label="Book File"
          id="book-file-upload"
          fileField="file"
          acceptedTypes={"application/pdf"}
          allowUrl
          urlField="fileUrl"
          getFieldValue={getFieldValue}
          onFileChange={handleFileChange}
          onUrlChange={handleChange}
          error={errors["fileUrl"]}
        />
        <FileUploadPreview
          label="Book Image"
          id="book-image-upload"
          fileField="image"
          acceptedTypes={"image/*"}
          allowUrl
          urlField="imageUrl"
          getFieldValue={getFieldValue}
          onFileChange={handleFileChange}
          onUrlChange={handleChange}
          error={errors["imageUrl"]}
        />

        <Divider variant="middle" className={styles.divider} />

        {/* Other Book Details */}
        <Box className={styles.section_container}>
          <Typography variant="h6" gutterBottom>
            {OTHER_BOOK_DETAILS_SECTION}
          </Typography>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                type="date"
                label="Publish Date"
                name="publishDate"
                className={styles.urlInputFields}
                value={getDate(getFieldValue("publishDate")) || ""}
                onChange={handleChange}
                error={!!errors["publishDate"]}
                helperText={errors["publishDate"]}
              />
              <TextField
                type="number"
                label="Number of Pages"
                name="noOfPages"
                className={styles.urlInputFields}
                value={getFieldValue("noOfPages") || ""}
                inputProps={{ min: 1 }}
                onChange={handleChange}
                error={!!errors["noOfPages"]}
                helperText={errors["noOfPages"]}
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                type="number"
                label="Part Number"
                name="partNo"
                className={styles.urlInputFields}
                value={getFieldValue("partNo") || ""}
                onChange={handleChange}
                style={{ flex: 1 }}
              />
            </Stack>
            <TextField
              fullWidth
              label="Remarks"
              name="remarks.en"
              value={getFieldValue("remarks.en") || ""}
              onChange={handleChange}
              multiline
              minRows={2}
              error={!!errors["remarks.en"]}
              helperText={errors["remarks.en"]}
            />
            <TextField
              fullWidth
              label="टिप्‍पणी"
              name="remarks.hi"
              value={getFieldValue("remarks.hi") || ""}
              onChange={handleChange}
              multiline
              minRows={2}
              error={!!errors["remarks.hi"]}
              helperText={errors["remarks.hi"]}
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="isFeatured"
                  checked={getFieldValue("isFeatured") || false}
                  onChange={handleChange}
                />
              }
              label={`Is Featured ${formTypeLabel}`}
            />
          </Stack>
        </Box>

        <Divider variant="middle" className={styles.divider} />

        {/* Meta Details Section */}
        <Box className={styles.section_container}>
          <Typography variant="h6" gutterBottom>
            {META_DETAILS}
          </Typography>
          <MetaFields
            getFieldValue={getFieldValue}
            handleChange={handleChange}
            handleFileChange={handleFileChange}
          />
        </Box>

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          {!isEditMode && (
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              onClick={handleResetForm}
              sx={{ flex: 1 }}
            >
              Reset Form
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            size="large"
            type="submit"
            disabled={isLoading}
            sx={{ flex: !isEditMode ? 2 : 1 }}
          >
            {isEditMode ? `Update ${formTypeLabel}` : `Add ${formTypeLabel}`}
          </Button>
        </Stack>
      </form>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Restore Draft Confirmation Dialog */}
      <CustomDialog
        open={draftDialogOpen}
        title="Restore Draft?"
        content="We found a saved draft for this form. Do you want to restore it?"
        primaryText="Restore"
        secondaryText="Delete Draft"
        onPrimaryClick={handleRestoreDraft}
        onSecondaryClick={handleDeleteDraft}
        onClose={() => setDraftDialogOpen(false)}
      />
    </Paper>
  );
};

export default BookForm;
